import React, { useState } from 'react'
import styled from 'styled-components'
import { PrimaryButton, SecondaryButton } from '../../styled/button'
import FormError from '../../styled/form-error'

/**
 * VersionManager Component
 *
 * Manages content versions for a song in the edit screen.
 * Allows users to:
 * - View all versions with labels and creation dates
 * - Select a version for editing
 * - Create new versions with custom labels (max 16 chars)
 * - Delete versions (with protection for the last version)
 *
 * Props:
 * - versions: Array of version objects with { versionId, label, isOriginal, createdAt }
 * - activeVersionId: ID of the currently selected version
 * - onVersionSelect: Callback when a version is clicked (versionId)
 * - onVersionCreate: Callback when creating a new version (label)
 * - onVersionDelete: Callback when deleting a version (versionId, label)
 * - canEdit: Boolean to show/hide the component (default: true)
 *
 * Requirements: 1.1, 1.2, 2.1, 2.4, 2.5
 */

const ManagerContainer = styled.div`
  margin-bottom: 1rem;
  padding: 0.75rem;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 0.25rem;
  background-color: ${({ theme }) => theme.secondaryBackground};
`

const ManagerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${({ theme }) => theme.borderColor};
`

const ManagerTitle = styled.h3`
  margin: 0;
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ theme }) => theme.bodyText};
`

const VersionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-bottom: 0.5rem;
`

const VersionItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background-color: ${({ theme, $active }) =>
    $active ? theme.primary : theme.inputBackground};
  border: 1px solid ${({ theme, $active }) =>
    $active ? theme.secondary : theme.borderColor};
  border-radius: 0.25rem;
  transition: all 0.15s ease-in-out;
  cursor: pointer;

  &:hover {
    border-color: ${({ theme }) => theme.secondary};
    background-color: ${({ theme, $active }) =>
      $active ? theme.primary : theme.secondaryBackground};
  }
`

const VersionInfo = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

const VersionLabel = styled.span`
  font-weight: ${({ $active }) => $active ? '600' : '400'};
  color: ${({ theme, $active }) =>
    $active ? theme.primaryContrast : theme.bodyText};
  font-size: 0.9rem;
`

const VersionMeta = styled.span`
  font-size: 0.7rem;
  color: ${({ theme, $active }) =>
    $active ? theme.primaryContrast : theme.secondaryText};
  opacity: 0.7;
`

const VersionActions = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`

const DeleteButton = styled(SecondaryButton)`
  padding: 0.25rem 0.5rem;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.danger || '#D8000C'};
  border-color: ${({ theme }) => theme.danger || '#D8000C'};

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.danger || '#D8000C'};
    color: white;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`

const CreateVersionForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  background-color: ${({ theme }) => theme.inputBackground};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 0.25rem;
  margin-top: 0.5rem;
`

const FormRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const Input = styled.input`
  flex: 1;
  padding: 0.5rem;
  font-size: 0.95rem;
  color: ${({ theme }) => theme.input};
  background-color: ${({ theme }) => theme.inputBackground};
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.secondary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.secondaryText};
    opacity: 0.6;
  }
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;

  @media (max-width: 768px) {
    width: 100%;

    button {
      flex: 1;
    }
  }
`

const CharCount = styled.span`
  font-size: 0.75rem;
  color: ${({ theme, $error }) =>
    $error ? (theme.danger || '#D8000C') : theme.secondaryText};
  align-self: flex-end;
`

const VersionManager = ({
  versions,
  activeVersionId,
  onVersionSelect,
  onVersionCreate,
  onVersionDelete,
  canEdit = true
}) => {
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newLabel, setNewLabel] = useState('')
  const [error, setError] = useState('')

  const handleCreateClick = () => {
    setShowCreateForm(true)
    setNewLabel('')
    setError('')
  }

  const handleCancelCreate = () => {
    setShowCreateForm(false)
    setNewLabel('')
    setError('')
  }

  const handleLabelChange = (e) => {
    const value = e.target.value
    setNewLabel(value)

    if (value.length > 16) {
      setError('Label must be 16 characters or less')
    } else if (value.trim().length === 0) {
      setError('Label cannot be empty')
    } else {
      setError('')
    }
  }

  const handleCreateSubmit = async () => {
    const trimmedLabel = newLabel.trim()

    if (trimmedLabel.length === 0) {
      setError('Label cannot be empty')
      return
    }

    if (trimmedLabel.length > 16) {
      setError('Label must be 16 characters or less')
      return
    }

    try {
      await onVersionCreate(trimmedLabel)
      setShowCreateForm(false)
      setNewLabel('')
      setError('')
    } catch (err) {
      setError(err.message || 'Failed to create version')
    }
  }

  const handleDeleteClick = async (versionId, label) => {
    try {
      await onVersionDelete(versionId, label)
    } catch (err) {
      setError(err.message || 'Failed to delete version')
    }
  }

  if (!canEdit) {
    return null
  }

  return (
    <ManagerContainer>
      <ManagerHeader>
        <ManagerTitle>Content Versions</ManagerTitle>
        {!showCreateForm && (
          <PrimaryButton onClick={handleCreateClick}>
            Create New Version
          </PrimaryButton>
        )}
      </ManagerHeader>

      <VersionList>
        {versions && versions.map((version) => (
          <VersionItem
            key={version.versionId}
            $active={version.versionId === activeVersionId}
            onClick={() => onVersionSelect(version.versionId)}
          >
            <VersionInfo>
              <VersionLabel $active={version.versionId === activeVersionId}>
                {version.label}
              </VersionLabel>
              <VersionMeta $active={version.versionId === activeVersionId}>
                {new Date(version.createdAt).toLocaleDateString()}
              </VersionMeta>
            </VersionInfo>
            {!version.isOriginal && (
              <VersionActions onClick={(e) => e.stopPropagation()}>
                <DeleteButton
                  onClick={() => handleDeleteClick(version.versionId, version.label)}
                  title={`Delete ${version.label}`}
                >
                  Delete
                </DeleteButton>
              </VersionActions>
            )}
          </VersionItem>
        ))}
      </VersionList>

      {showCreateForm && (
        <CreateVersionForm>
          <FormRow>
            <Input
              type='text'
              value={newLabel}
              onChange={handleLabelChange}
              placeholder='Enter version label (e.g., Bass, Guitar, Piano)'
              maxLength={17}
              autoFocus
            />
          </FormRow>
          <FormRow>
            <CharCount $error={newLabel.length > 16}>
              {newLabel.length}/16 characters
            </CharCount>
          </FormRow>
          {error && <FormError>{error}</FormError>}
          <ButtonGroup>
            <PrimaryButton
              onClick={handleCreateSubmit}
              disabled={error || newLabel.trim().length === 0}
            >
              Create Version
            </PrimaryButton>
            <SecondaryButton onClick={handleCancelCreate}>
              Cancel
            </SecondaryButton>
          </ButtonGroup>
        </CreateVersionForm>
      )}
    </ManagerContainer>
  )
}

export default VersionManager
